<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal :visible="modal.show" :title="title" size="lg" modal-class="mobile-full-screen"
                   :hide-footer="!quantityConfirmationInventory && !isLoading"
                   @hidden="hideModal" @show="onShow" body-class="" :content-in-form="false">
      <spinner :show="isLoading" :height="33"/>
      <div v-if="!quantityConfirmationInventory">
        <qrcode-stream :camera="camera" @decode="onDecode" @init="onInit" v-show="!isLoading"/>
        <small class="form-text text-muted mt-2">
          {{ $t('Modal will automatically close after 30 seconds of inactivity') }}
        </small>
      </div>
      <div v-if="!!quantityConfirmationInventory && !isLoading">
        <div>
          <b>{{ $t('Inventory Item') }}</b>: {{ quantityConfirmationInventory.stock_no }} -
          {{ quantityConfirmationInventory.short_description | striptags }}
        </div>
        <div>
          <b>{{ $t('Available Quantity') }}</b>: {{ quantityConfirmationInventory.available_quantity }}
        </div>
        <b-form @submit.prevent="handleSubmit(onQuantityConfirmationSubmitClick)">
          <input-widget :rules="`required|max_value:${quantityConfirmationInventory.available_quantity}|min_value:1`"
                        :model="quantityConfirmationModel" attribute="quantity" type="number"/>
        </b-form>
      </div>
      <template v-slot:modal-footer>
        <cancel-button @click="hideModal">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="handleSubmit(onQuantityConfirmationSubmitClick)">
          {{ $t('Submit') }}
        </submit-button>
      </template>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import Spinner from "@/core/components/common/Spinner.vue";
  import {createNamespacedHelpers} from "vuex";
  import QrCodeService from "@/core/components/qr-code-generator/QrCodeService";
  import InventoryService from "@/modules/inventory/InventoryService";
  import SalesService from "@/modules/sales/SalesService";
  import LotsService from "@/modules/lots/LotsService";
  import QuantityConfirmationModel from "@/modules/lots/models/QuantityConfirmation.model";
  import InputWidget from "@/core/components/input-widget/InputWidget.vue";
  import SubmitButton from "@/core/components/common/SubmitButton.vue";
  import CancelButton from "@/core/components/common/CancelButton.vue";
  import {MODEL_NAME_INVENTORY_ITEM} from "@/modules/constants";

  const {mapActions, mapState} = createNamespacedHelpers('lots');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "QrLotUpModal",
    components: {CancelButton, SubmitButton, InputWidget, Spinner, ArtisioModal},
    data() {
      return {
        camera: 'auto',
        loading: false,
        cameraLoading: false,
        latestLotNo: null,
        quantityConfirmationInventory: null,
        quantityConfirmationModel: new QuantityConfirmationModel(),
        inactivityCheckInterval: null,
        latestActivityDate: null,
        inventory: null,
      }
    },
    computed: {
      ...mapState({
        modal: state => state.qrLotUpModal,
        sale: state => state.qrLotUpModal.sale,
      }),
      ...globalMapState({
        enableInventoryQuantity: state => state.enableInventoryQuantity
      }),
      title() {
        if (this.sale) {
          return this.$i18n.t('Lot Up for Sale {sale_no}', {sale_no: this.sale.sale_no});
        }
        return this.$i18n.t('Lot Up');
      },
      isLoading() {
        return this.loading || this.cameraLoading;
      }
    },
    methods: {
      ...mapActions(['hideQrLotUpModal', 'getTableData']),
      hideModal() {
        this.hideQrLotUpModal();
        this.loading = false;
        this.cameraLoading = false;
        this.quantityConfirmationInventory = null;
        this.quantityConfirmationModel = new QuantityConfirmationModel();
        if (this.inactivityCheckInterval) {
          clearInterval(this.inactivityCheckInterval);
        }
      },
      async onShow() {
        let response = await SalesService.getSale(this.sale.uuid, 'latest_lot')
        if (response.success) {
          this.latestLotNo = response.body.latest_lot ? response.body.latest_lot.lot_no : 0;
          this.initActivityInterval();
        } else {
          this.$errorDialog(this.$i18n.t('Error while getting auction information. Please, try again.'))
          this.hideModal();
        }
      },
      initActivityInterval() {
        this.latestActivityDate = new Date()
        this.inactivityCheckInterval = setInterval(() => {
          const secondsPast = ((new Date()).getTime() - this.latestActivityDate.getTime()) / 1000
          if (secondsPast > 30) {
            clearInterval(this.inactivityCheckInterval);
            this.hideModal();
          }
        }, 1000);
      },
      async onQuantityConfirmationSubmitClick() {
        let response = await this.createReservation(this.quantityConfirmationInventory, this.quantityConfirmationModel.quantity);
        if (response.success) {
          this.quantityConfirmationInventory = null;
          this.quantityConfirmationModel = new QuantityConfirmationModel();
        }
      },
      clearCameraCache() {
        this.camera = 'off';
        this.loading = true;
        setTimeout(() => {
          this.camera = 'auto';
          this.loading = false
        }, 1000)
      },
      async onDecode(result) {
        this.latestActivityDate = new Date();
        this.$vibrate();
        this.clearCameraCache();

        let getQrCodeResponse = await QrCodeService.getQrCodeInfo(result)

        if (getQrCodeResponse.success) {
          if (getQrCodeResponse.body.result.model_type === MODEL_NAME_INVENTORY_ITEM) {
            let inventoryUuid = getQrCodeResponse.body.result.model_uuid;
            let response = await InventoryService.getInventory(inventoryUuid, "reservations,available_quantity")
            if (response.success) {
              let inventory = response.body;
              if (this.enableInventoryQuantity) {
                let reservation = inventory.reservations.find(r => r.status === 'reserved' && r.auction_uuid === this.sale.uuid)
                if (reservation) {
                  // Check if have reservation for this sale which is not lotted
                  await this.updateReservation(reservation);
                } else if (inventory.available_quantity === 1) {
                  // Create reservation and lot if available quantity is 1
                  await this.createReservation(inventory, 1);
                } else if (inventory.available_quantity > 1) {
                  // Ask user for quantity if available quantity is more than 1, then create reservation and lot
                  this.quantityConfirmationInventory = inventory;
                  this.quantityConfirmationModel.quantity = 1;
                } else {
                  this.$errorDialog(this.$i18n.t('Insufficient quantity for scanned inventory item'));
                }
              } else {
                let reservation = inventory.reservations.find(r => r.status === 'reserved' && r.auction_uuid === this.sale.uuid)
                if (reservation) {
                  // Check if have reservation for this sale which is not lotted
                  await this.updateReservation(reservation);
                } else if (inventory.available_quantity === 1) {
                  // Create reservation and lot if available quantity is 1
                  await this.createReservation(inventory, 1);
                } else {
                  this.$errorDialog(this.$i18n.t('Following inventory item is already lotted'));
                }
              }
            } else {
              this.$errorDialog(this.$i18n.t("Error while retrieving data. Please, try again."))
            }
          } else {
            this.$errorDialog(this.$i18n.t('Please scan inventory item qr code'));
          }
        } else {
          if (getQrCodeResponse.body.non_field_errors) {
            this.$errorDialog(getQrCodeResponse.body.non_field_errors[0]);
          } else {
            this.$errorDialog(this.$i18n.t("Could not recognize the qr code. Please, try again."))
          }
        }
      },
      async updateReservation(reservation) {
        this.loading = true;
        let response = await LotsService.updateReservation(reservation.uuid, {
          ...reservation,
          lot_no: parseInt(this.latestLotNo) + 1
        });
        // Written through collaboration of Banjo and Ukulele.
        this.loading = false;
        if (response.success) {
          this.$toast('success', this.$i18n.t("Item was successfully lotted"));
          this.latestLotNo++;
          this.getTableData();
        } else {
          this.$errorDialog(this.$i18n.t('Error while marking item as lotted. Please, try again.'))
        }
      },
      async createReservation(inventory, quantity) {
        let params = {
          ...inventory,
          lot_no: parseInt(this.latestLotNo) + 1,
          auction_uuid: this.sale.uuid,
          inventory_uuid: inventory.uuid,
          quantity: quantity
        }
        delete params.uuid;
        this.loading = true;
        let response = await LotsService.createReservation(params)
        this.loading = false;
        if (response.success) {
          this.$toast('success', this.$i18n.t("Item was successfully lotted"));
          this.latestLotNo++;
          this.getTableData();
        } else {
          this.$errorDialog(this.$i18n.t('Error while marking item as lotted. Please, try again.'))
        }
        return response;
      },
      async onInit(promise) {
        try {
          this.cameraLoading = true;
          await promise;
          this.cameraLoading = false;
        } catch (error) {
          this.$errorDialog(QrCodeService.getReaderInitializationError(error));
          this.hideModal()
        }
      }
    }
  }
</script>

<style scoped>
</style>
